import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Chip,
  Skeleton,
  alpha,
  useTheme,
  IconButton,
  Tooltip,
  Fade,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  Event as EventIcon,
  Whatshot as FireIcon,
  Cloud as SmokeIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { getEvents, selectEvents, resetEvents } from "./LiveSlice";
import {
  selectFilters,
  setFilterApplied,
} from "../features/filters/LiveFilterSlice";
import { LiveFilter } from "../features/filters/LiveFilter";
import LiveWeather from "./LiveWeather";

function LiveEvents(props) {
  const { cam_id, eventClick: propsEventClick } = props;
  const theme = useTheme();
  const [state, setState] = useState({ page: 0, rowsPerPage: 10 });
  const [selectedRow, setSelectedRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const isFirstRender = useRef(true);
  const { results: events, count } = useSelector(selectEvents);
  const filters = useSelector(selectFilters);
  const dispatch = useDispatch();
  const { page, rowsPerPage } = state;

  // Load events when filters or page changes
  useEffect(() => {
    const loadEvents = async () => {
      setLoading(true);
      if (isFirstRender.current) {
        isFirstRender.current = false;
        await dispatch(
          getEvents(1, filters.filterApplied, rowsPerPage, cam_id),
        );
      } else {
        await dispatch(
          getEvents(page + 1, filters.filterApplied, rowsPerPage, cam_id),
        );
        dispatch(setFilterApplied(false));
      }
      setLoading(false);
    };
    loadEvents();
  }, [filters, cam_id, page, rowsPerPage, dispatch]);

  // Auto-select first event when events load
  useEffect(() => {
    if (events.length > 0 && !selectedRow && !loading) {
      propsEventClick(events[0]);
      setSelectedRow(events[0].uuid);
    }
  }, [events, propsEventClick, selectedRow, loading]);

  const handleChangePage = useCallback((event, newPage) => {
    setState((prevState) => ({ ...prevState, page: newPage }));
    setSelectedRow(null);
  }, []);

  const handleChangeRowsPerPage = useCallback(
    (event) => {
      const newRowsPerPage = parseInt(event.target.value, 10);
      setState({ page: 0, rowsPerPage: newRowsPerPage });
      setSelectedRow(null);
      dispatch(resetEvents());
    },
    [dispatch],
  );

  const handleEventClick = useCallback(
    (row) => {
      propsEventClick(row);
      setSelectedRow(row.uuid);
    },
    [propsEventClick],
  );

  const handleRefresh = useCallback(() => {
    dispatch(resetEvents());
    setSelectedRow(null);
    dispatch(getEvents(1, filters.filterApplied, rowsPerPage, cam_id));
  }, [dispatch, filters.filterApplied, rowsPerPage, cam_id]);

  // Get event type icon
  // const getEventIcon = (species) => {
  //   if (!species || species.length === 0) return <EventIcon fontSize="small" />;
  //   const hasFire = species.some(
  //     (s) => s.id === "fire" || s.name?.toLowerCase() === "fire",
  //   );
  //   const hasSmoke = species.some(
  //     (s) => s.id === "smoke" || s.name?.toLowerCase() === "smoke",
  //   );
  //   if (hasFire && hasSmoke)
  //     return <FireIcon fontSize="small" sx={{ color: "#ff6b6b" }} />;
  //   if (hasFire) return <FireIcon fontSize="small" sx={{ color: "#ff4444" }} />;
  //   if (hasSmoke)
  //     return <SmokeIcon fontSize="small" sx={{ color: "#ff9933" }} />;
  //   return <EventIcon fontSize="small" />;
  // };

  // Loading skeleton
  if (loading && events.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Events
          </Typography>
          <Skeleton variant="circular" width={32} height={32} />
        </Box>
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} variant="rounded" height={80} sx={{ mb: 1 }} />
        ))}
      </Box>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <EventIcon sx={{ color: theme.palette.primary.main }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Recent Events
          </Typography>
          {events.length > 0 && (
            <Chip
              label={count || events.length}
              size="small"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                fontWeight: 600,
              }}
            />
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LiveFilter />
          <Tooltip title="Refresh Events">
            <IconButton size="small" onClick={handleRefresh}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Events Table */}
      {events.length !== 0 ? (
        <>
          <TablePagination
            component="div"
            rowsPerPageOptions={[10, 20, 50, 100]}
            count={count !== null ? count : 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onRowsPerPageChange={handleChangeRowsPerPage}
            onPageChange={handleChangePage}
            showFirstButton
            sx={{
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              "& .MuiTablePagination-toolbar": {
                minHeight: 48,
              },
            }}
          />
          <TableContainer sx={{ flex: 1, overflowY: "auto" }}>
            <Table size="small" stickyHeader>
              <TableBody>
                {(rowsPerPage > 0
                  ? events.slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage,
                    )
                  : events
                ).map((row, index) => {
                  const isSelected = selectedRow === row.uuid;
                  const hasFire = row.species?.some(
                    (s) => s.id === "fire" || s.name?.toLowerCase() === "fire",
                  );
                  const hasSmoke = row.species?.some(
                    (s) =>
                      s.id === "smoke" || s.name?.toLowerCase() === "smoke",
                  );

                  return (
                    <TableRow
                      key={row.uuid}
                      onClick={() => handleEventClick(row)}
                      sx={{
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        bgcolor: isSelected
                          ? alpha(theme.palette.primary.main, 0.08)
                          : "transparent",
                        "&:hover": {
                          bgcolor: alpha(theme.palette.primary.main, 0.04),
                        },
                      }}
                    >
                      <TableCell sx={{ p: 0 }}>
                        <Fade in timeout={300}>
                          <Box sx={{ position: "relative" }}>
                            {/* Event Type Indicator */}
                            <Box
                              sx={{
                                position: "absolute",
                                left: 8,
                                top: 12,
                                zIndex: 1,
                              }}
                            >
                              {hasFire && hasSmoke ? (
                                <Tooltip title="Fire & Smoke Detected">
                                  <Chip
                                    size="small"
                                    icon={<FireIcon sx={{ fontSize: 14 }} />}
                                    label="FIRE+SMOKE"
                                    sx={{
                                      height: 20,
                                      fontSize: 10,
                                      fontWeight: 700,
                                      bgcolor: "#ff4444",
                                      color: "white",
                                      "& .MuiChip-icon": {
                                        color: "white",
                                        fontSize: 12,
                                      },
                                    }}
                                  />
                                </Tooltip>
                              ) : hasFire ? (
                                <Tooltip title="Fire Detected">
                                  <Chip
                                    size="small"
                                    icon={<FireIcon sx={{ fontSize: 14 }} />}
                                    label="FIRE"
                                    sx={{
                                      height: 20,
                                      fontSize: 10,
                                      fontWeight: 700,
                                      bgcolor: "#ff4444",
                                      color: "white",
                                      "& .MuiChip-icon": {
                                        color: "white",
                                        fontSize: 12,
                                      },
                                    }}
                                  />
                                </Tooltip>
                              ) : hasSmoke ? (
                                <Tooltip title="Smoke Detected">
                                  <Chip
                                    size="small"
                                    icon={<SmokeIcon sx={{ fontSize: 14 }} />}
                                    label="SMOKE"
                                    sx={{
                                      height: 20,
                                      fontSize: 10,
                                      fontWeight: 700,
                                      bgcolor: "#ff9933",
                                      color: "white",
                                      "& .MuiChip-icon": {
                                        color: "white",
                                        fontSize: 12,
                                      },
                                    }}
                                  />
                                </Tooltip>
                              ) : null}
                            </Box>
                            <LiveWeather data={row} />
                          </Box>
                        </Fade>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 8,
            px: 2,
            textAlign: "center",
          }}
        >
          <EventIcon
            sx={{
              fontSize: 48,
              color: alpha(theme.palette.text.secondary, 0.3),
              mb: 2,
            }}
          />
          <Typography variant="body1" color="text.secondary" gutterBottom>
            No Events Found
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Try adjusting your filters or check back later
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

export default LiveEvents;
